// APPROVALS surface (N-APPROVE-1), recreated 1:1 from the prototype (screens.js approvals): the sticky
// toolbar carries the Mine filter (honest bound-to-me count), the waiting stamp, and the info affordance;
// the body is the stat strip and the authority-first triage groups. The single inbox of every pending human Gate
// the AUTHENTICATED human is authorised for, aggregated across modules. Exercising a Gate ROUTES an
// authenticated action to the OWNING module's write interface (approveTaskStart / activateMetric /
// approveMetricValue) — the dashboard NEVER sets an approval field and never accepts one from the client
// (P-GATE, AC-3). A Task-start Gate is offered ONLY to the bound owner (AC-4): a non-bound item shows no
// exercise control (the OWNING module is the boundary, G-180). Only the FIRST bound pending gate carries
// the primary button (brief §4).
//
// Thin over services; the same GateList the Today "Needs you" list uses renders the inbox (one delivery
// path for the exercise — AR-MOD-4). Tokens only (G-181). After a routed exercise the surface re-reads.

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { GateList } from './components/GateList.tsx';
import { StatStrip, StatCard, SectionHead } from './primitives.tsx';
import { ScreenToolbar, ScreenBody, TbFilter, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import type { ApprovalsInboxProjection, ApprovalGateKind } from './projections.ts';
import type { RouteResult } from './api-client.ts';
import { APPROVAL_GATE_KINDS, APPROVAL_GATE_LABELS } from './approval-gate-labels.ts';
import {
  createApprovalTriage,
  type ApprovalTriageGroup,
  type ApprovalTriageSummary,
} from './approval-triage.ts';

/** The two routed gate intents (exercise + decline), each re-reading on ok so the rendered inbox
 *  derives from the durable lifecycle (G-182 — a declined task-start leaves pending). */
function useGateRoutes(reload: () => void): {
  readonly onExercise: (
    kind: ApprovalGateKind,
    objectId: string,
    expectedRevision: number | null,
  ) => Promise<RouteResult>;
  readonly onDecline: (
    kind: ApprovalGateKind,
    objectId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
  readonly onRequestChanges: (
    scheduleId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
} {
  const api = useSurfaceApi();
  const onExercise = useCallback(
    async (kind: ApprovalGateKind, objectId: string, expectedRevision: number | null) => {
      const result = await api.exerciseGate(kind, objectId, expectedRevision);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );
  // The DECLINE leg (BUILD-DEC-116): routed with the mandatory reason (G-055).
  const onDecline = useCallback(
    async (kind: ApprovalGateKind, objectId: string, reason: string, expectedRevision?: number) => {
      const result = await api.declineGate(kind, objectId, reason, expectedRevision);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );
  const onRequestChanges = useCallback(
    async (scheduleId: string, reason: string, expectedRevision?: number) => {
      if (expectedRevision === undefined) {
        return { ok: false, note: 'Refresh this schedule before requesting changes.' };
      }
      const result = await api.requestScheduleChanges(scheduleId, expectedRevision, reason);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );
  return { onExercise, onDecline, onRequestChanges };
}

function toggledKinds(
  current: readonly ApprovalGateKind[],
  kind: ApprovalGateKind,
): readonly ApprovalGateKind[] {
  return current.includes(kind)
    ? current.filter((candidate) => candidate !== kind)
    : APPROVAL_GATE_KINDS.filter((candidate) => candidate === kind || current.includes(candidate));
}

/** The Approvals inbox surface. */
export function ApprovalsSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<ApprovalsInboxProjection>(api.getApprovals);
  const [mineOnly, setMineOnly] = useState(false);
  const [titleSearch, setTitleSearch] = useState('');
  const [selectedKinds, setSelectedKinds] = useState<readonly ApprovalGateKind[]>([]);
  const { onExercise, onDecline, onRequestChanges } = useGateRoutes(state.reload);

  const summary = state.data === null ? null : createApprovalTriage(state.data.items).summary;
  const toggleKind = (kind: ApprovalGateKind): void => {
    setSelectedKinds((current) => toggledKinds(current, kind));
  };

  return (
    <>
      <ScreenToolbar title="Approvals">
        <TbFilter
          label="Mine"
          count={summary?.mine ?? 0}
          on={mineOnly}
          onClick={() => setMineOnly((on) => !on)}
        />
        {summary !== null ? <TbStamp>{summary.total} waiting</TbStamp> : null}
        <TbInfo
          title="Approvals"
          tip="Pending gates are grouped by your authority first, then exact readiness. Search and gate-type filters never change the underlying queue."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(inbox) => (
            <ApprovalsBody
              inbox={inbox}
              mineOnly={mineOnly}
              titleSearch={titleSearch}
              selectedKinds={selectedKinds}
              onTitleSearch={setTitleSearch}
              onToggleKind={toggleKind}
              onExercise={onExercise}
              onDecline={onDecline}
              onRequestChanges={onRequestChanges}
            />
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** Summarise the loaded inbox without duplicating Gate readiness rules. */
function ApprovalStats({
  summary,
}: {
  readonly summary: ApprovalTriageSummary;
}): React.JSX.Element {
  return (
    <StatStrip>
      <StatCard
        label="Pending"
        value={summary.total}
        note="gates waiting"
        dotTone={summary.total > 0 ? 'attention' : undefined}
      />
      <StatCard label="Ready now" value={summary.readyForYou} note="bound and ready for you" />
      <StatCard label="Setup needed" value={summary.setupNeeded} note="bound but not ready" />
      <StatCard label="With someone else" value={summary.withSomeoneElse} note="not bound to you" />
    </StatStrip>
  );
}

interface ApprovalsBodyProps {
  readonly inbox: ApprovalsInboxProjection;
  readonly mineOnly: boolean;
  readonly titleSearch: string;
  readonly selectedKinds: readonly ApprovalGateKind[];
  readonly onTitleSearch: (value: string) => void;
  readonly onToggleKind: (kind: ApprovalGateKind) => void;
  readonly onExercise: (
    kind: ApprovalGateKind,
    objectId: string,
    expectedRevision: number | null,
  ) => Promise<RouteResult>;
  readonly onDecline: (
    kind: ApprovalGateKind,
    objectId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
  readonly onRequestChanges: (
    scheduleId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
}

function ApprovalFilters({
  titleSearch,
  selectedKinds,
  onTitleSearch,
  onToggleKind,
}: Pick<
  ApprovalsBodyProps,
  'titleSearch' | 'selectedKinds' | 'onTitleSearch' | 'onToggleKind'
>): React.JSX.Element {
  return (
    <div className="mb-6" aria-label="Approval filters">
      <label className="block max-w-xl text-sm text-text" htmlFor="approval-title-search">
        Search approval titles
        <input
          id="approval-title-search"
          type="search"
          value={titleSearch}
          onChange={(event) => onTitleSearch(event.target.value)}
          className="mt-2 w-full border border-line bg-panel px-3 py-2 text-sm text-text outline-none focus:border-forest"
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Gate type filters">
        {APPROVAL_GATE_KINDS.map((kind) => {
          const selected = selectedKinds.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              className={selected ? 'tb-filter on' : 'tb-filter'}
              aria-pressed={selected}
              onClick={() => onToggleKind(kind)}
            >
              {APPROVAL_GATE_LABELS[kind]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const GROUP_EMPTY_TEXT: Readonly<Record<ApprovalTriageGroup['id'], string>> = {
  ready_for_you: 'No matching approvals are ready for you.',
  setup_needed: 'No matching approvals need setup.',
  with_someone_else: 'No matching approvals are with someone else.',
};

function ApprovalGroupSection({
  group,
  onExercise,
  onDecline,
  onRequestChanges,
}: Pick<ApprovalsBodyProps, 'onExercise' | 'onDecline' | 'onRequestChanges'> & {
  readonly group: ApprovalTriageGroup;
}): React.JSX.Element {
  return (
    <section
      aria-label={`${group.label} approvals`}
      className="group"
      data-approval-group={group.id}
    >
      <SectionHead
        label={group.label}
        right={<span className="pill neutral">{group.items.length}</span>}
      />
      <div className="panel">
        <GateList
          variant="appr"
          gates={group.items}
          emptyText={GROUP_EMPTY_TEXT[group.id]}
          onExercise={onExercise}
          onDecline={onDecline}
          onRequestChanges={onRequestChanges}
        />
      </div>
    </section>
  );
}

/** The loaded Approvals body — full counts plus filtered, authority-first triage groups. */
function ApprovalsBody({
  inbox,
  mineOnly,
  titleSearch,
  selectedKinds,
  onTitleSearch,
  onToggleKind,
  onExercise,
  onDecline,
  onRequestChanges,
}: ApprovalsBodyProps): React.JSX.Element {
  const triage = createApprovalTriage(inbox.items, { titleSearch, kinds: selectedKinds, mineOnly });
  return (
    <>
      <ApprovalStats summary={triage.summary} />

      <section aria-label="Pending approvals" className="group">
        <SectionHead
          label="Decision queue"
          right={
            <span className="pill pending">
              <span className="dot" aria-hidden="true" />
              {triage.shown} shown of {triage.summary.total}
            </span>
          }
        />
        <ApprovalFilters
          titleSearch={titleSearch}
          selectedKinds={selectedKinds}
          onTitleSearch={onTitleSearch}
          onToggleKind={onToggleKind}
        />
        {triage.groups.map((group) => (
          <ApprovalGroupSection
            key={group.id}
            group={group}
            onExercise={onExercise}
            onDecline={onDecline}
            onRequestChanges={onRequestChanges}
          />
        ))}
      </section>
    </>
  );
}
